/**
 * SendForReviewDialog — opens from My Posts bulk-action toolbar.
 *
 * Lets the agency owner package selected drafts and email them to a client
 * via a magic-link review URL. Either picks a saved client (auto-fills the
 * email) or types a free-form recipient address.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Send, Loader2, Copy, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';

export const SendForReviewDialog = ({ open, onOpenChange, postIds, onSent }) => {
  const { api } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('__none__');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get('/clients');
        setClients(r.data?.clients || []);
      } catch (_) { /* fail-soft */ }
    })();
    // Reset state when dialog opens fresh
    setResult(null);
    setMessage('');
    setRecipientEmail('');
    setClientId('__none__');
  }, [open, api]);

  // Auto-fill the recipient when an explicit client with review_email is picked
  const handleClientChange = (val) => {
    setClientId(val);
    const c = clients.find((x) => x.id === val);
    if (c?.review_email) setRecipientEmail(c.review_email);
  };

  const handleSend = async () => {
    if (postIds.length === 0) {
      toast.error('No posts selected');
      return;
    }
    if (!recipientEmail.trim() && clientId === '__none__') {
      toast.error('Pick a client with a review email or type a recipient');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        post_ids: postIds,
        message: message.trim() || null,
      };
      if (clientId !== '__none__') payload.client_id = clientId;
      if (recipientEmail.trim()) payload.recipient_email = recipientEmail.trim();
      const r = await api.post('/reviews/send', payload);
      setResult(r.data);
      toast.success(`Review link sent to ${r.data.recipient_email}`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send for review');
    } finally {
      setSubmitting(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      toast.success('Link copied');
    } catch (_) { /* no-op */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="send-review-dialog">
        <DialogHeader>
          <DialogTitle>Send {postIds.length} post{postIds.length === 1 ? '' : 's'} for review</DialogTitle>
          <DialogDescription>
            Your client will receive a magic link — no account required. Link expires in 7 days.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Client (optional)</Label>
              <Select value={clientId} onValueChange={handleClientChange}>
                <SelectTrigger data-testid="review-client-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— No client (manual email) —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.review_email ? ` · ${c.review_email}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="review-recipient">Recipient email</Label>
              <Input
                id="review-recipient"
                type="email"
                placeholder="client@acme.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                data-testid="review-recipient-input"
              />
              <p className="text-xs text-zinc-500">
                Overrides the client's saved review email when set.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="review-message">Message (optional)</Label>
              <Textarea
                id="review-message"
                rows={3}
                placeholder="Hi Jane — please review these by Friday. Thanks!"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                data-testid="review-message-input"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="send-review-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <Check className="w-4 h-4 text-emerald-600" />
                <b className="text-emerald-900">Review link sent</b>
              </div>
              <p className="text-emerald-800 text-xs">
                Sent to <b>{result.recipient_email}</b>. Expires {new Date(result.expires_at).toLocaleDateString()}.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Shareable link</Label>
              <div className="flex gap-2">
                <Input value={result.url} readOnly className="text-xs" data-testid="review-result-url" />
                <Button variant="outline" size="icon" onClick={copyUrl} title="Copy">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" asChild title="Open">
                  <a href={result.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={handleSend} disabled={submitting} data-testid="send-review-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send link
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} data-testid="send-review-close">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendForReviewDialog;
